import React, { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import './index.less';

import IbButton from '../../../../components/common/IbButton';
import IbModal, { IbModalPosition } from '../../../../components/common/IbModal';
import { AgentCreationRequest, AgentModel, AgentUpdatingRequest, DataInterfaceType } from '../../../../../gamma-api';
import IbTypography from '../../../../components/common/IbTypography';
import IbInput from '../../../../components/common/IbInput';
import { ValidationErrors } from '../../../../types';
import { useValidationForm, CheckFieldValueFnMap } from '../../../../utils/validationUtil';
import { getTranslateFunc } from '../../../../utils/stringUtil';
import { currentFolderIdSelector } from '../../../../recoil';

import { FORM_ITEM_CLASS_NAME, MAIN_CLASS_NAME, EMPTY_AGENT } from './const';

type AgentForm = {
  name: string;
};

const checkFieldValueFnMap: CheckFieldValueFnMap<AgentForm> = {
  name: (value: string) => !!(value || '').trim(),
};

interface IAgentModalProps {
  interfaceType?: DataInterfaceType;
  visible: boolean;
  editAgent?: AgentModel;
  onAdd?: (agent: AgentCreationRequest, onSubmitErrors: (errors: ValidationErrors) => void) => Promise<void>;
  onEdit?: (agent: AgentUpdatingRequest, onSubmitErrors: (errors: ValidationErrors) => void) => Promise<void>;
  onCancel?: () => void;
}

const AgentModal: React.FC<IAgentModalProps> = ({ interfaceType, visible, editAgent, onAdd, onEdit, onCancel }) => {
  const emptyAgent: AgentModel = {
    ...EMPTY_AGENT,
    settings: {
      ...EMPTY_AGENT.settings,
      interface: { ...EMPTY_AGENT.settings.interface, type: interfaceType || EMPTY_AGENT.settings.interface.type },
    },
  };

  const folderId = useRecoilValue(currentFolderIdSelector);

  const [saving, setSaving] = useState(false);
  const [agent, setAgent] = useState<AgentModel>(emptyAgent);

  const initialFormValues: AgentForm = {
    name: editAgent?.name || '',
  };

  const {
    validationState,
    onFieldChange,
    onFieldBlur,
    onSubmitErrors,
    validate,
    reset,
    getShowError,
    getErrorMessage,
  } = useValidationForm<AgentForm>(initialFormValues, checkFieldValueFnMap);

  const translatePrefix = interfaceType === DataInterfaceType.Api ? 'API' : 'Chat';
  const translate = getTranslateFunc(`Agent: ${translatePrefix}: Modal:`);

  const onVisiblePropChange = () => {
    if (!visible) return;

    setSaving(false);
    setAgent(editAgent || emptyAgent);
    reset();
  };
  useEffect(onVisiblePropChange, [visible]);

  const onNameChange = (value: string) => {
    setAgent({ ...agent, name: value });
    onFieldChange('name', value);
  };

  const onDisplayNameChange = (value: string) => {
    setAgent({ ...agent, settings: { ...agent.settings, widget: { ...agent.settings.widget, display_name: value } } });
  };

  const onNameBlur = () => onFieldBlur('name');

  const onCreateButtonClick = async () => {
    if (!onAdd || !validate()) return;

    setSaving(true);
    await onAdd(
      {
        folder_id: folderId,
        name: agent.name,
        state: agent.state,
        settings: {
          ...agent.settings,
          widget: {
            ...agent.settings.widget,
            display_name: agent.settings.widget.display_name || agent.name,
          },
        },
        trace: agent.trace,
      },
      onSubmitErrors
    );
    setSaving(false);
  };

  const onSaveButtonClick = async () => {
    if (!onEdit || !editAgent || !validate()) return;

    setSaving(true);
    await onEdit(
      { name: agent.name, state: agent.state, settings: agent.settings, trace: agent.trace },
      onSubmitErrors
    );
    setSaving(false);
  };

  const renderModalContent = () => {
    return (
      <>
        <div className={FORM_ITEM_CLASS_NAME}>
          <IbTypography.Paragraph type="secondary">{translate('Name: Title')}</IbTypography.Paragraph>
          <IbInput
            status={getShowError(validationState.name) ? 'error' : 'default'}
            value={agent.name}
            onBlur={onNameBlur}
            onChange={onNameChange}
          />
          {getShowError(validationState.name) && (
            <IbTypography.Paragraph error type="descriptor">
              {getErrorMessage(validationState.name, translate('Name: Error'))}
            </IbTypography.Paragraph>
          )}
        </div>
        {interfaceType === DataInterfaceType.Chat && (
          <div className={FORM_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph type="secondary">{translate('Display name: Title')}</IbTypography.Paragraph>
            <IbInput value={agent.settings.widget.display_name} onChange={onDisplayNameChange} />
          </div>
        )}
      </>
    );
  };

  const renderFooter = () => {
    return (
      <>
        {editAgent ? (
          <IbButton type="primary" onClick={onSaveButtonClick}>
            {translate('Save')}
          </IbButton>
        ) : (
          <IbButton type="primary" onClick={onCreateButtonClick}>
            {translate('Create')}
          </IbButton>
        )}
        <IbButton type="secondary" onClick={onCancel}>
          {translate('Cancel')}
        </IbButton>
      </>
    );
  };

  const title = editAgent ? translate('Edit title') : translate('Create title');

  return (
    <IbModal
      className={MAIN_CLASS_NAME}
      footer={renderFooter()}
      loading={saving}
      position={IbModalPosition.Centered}
      title={title}
      visible={visible}
      onCancel={onCancel}
    >
      {renderModalContent()}
    </IbModal>
  );
};

export default AgentModal;
